// frontend/src/pages/employees/usePeriod.js
//
// Day / month / year period state, and the date arithmetic behind it.
//
// EVERY calculation here is string arithmetic on "YYYY-MM-DD". This is
// not a style preference: `new Date("2026-09-29")` parses as UTC, so in
// Saskatchewan it is the 28th at 6pm, and every boundary lands a day
// early. TimeEntryPage.jsx already does its own string math for exactly
// this reason -- this module follows that precedent.
//
// The one permitted exception is the three-argument Date constructor,
// `new Date(2026, 8, 15)`, which is local-time and safe. It is used
// below for ONE purpose: reading a weekday name out of Intl. It never
// produces a date this module returns.
import { useCallback, useMemo } from "react";

export const GRANULARITIES = ["day", "month", "year", "custom"];

const MONTHS = ["January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"];
const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday",
  "Friday", "Saturday"];

const pad = (n) => String(n).padStart(2, "0");

export function daysInMonth(year, month) {
  if (month === 2) {
    const leap = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
    return leap ? 29 : 28;
  }
  return [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][month - 1];
}

export function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function parts(iso) {
  const [y, m, d] = iso.split("-").map(Number);
  return { y, m, d };
}

// A custom anchor packs two ISO dates into one string -- "FROM..TO" -- so
// the rest of the app keeps its single `d` URL parameter unchanged. This
// is the ONLY place that string gets split. Every exported function below
// branches on granularity === "custom" and returns via this helper (or
// the arithmetic built on it) *before* it can reach parts(), which would
// otherwise choke on the ".." and produce NaN-laced garbage silently.
function splitCustom(anchor) {
  const [from, to] = String(anchor).split("..");
  return { from, to };
}

// Howard Hinnant's days-from-civil algorithm: pure integer math on
// y/m/d, proleptic Gregorian, no Date object involved. Used only to
// measure the length of a custom window so stepPeriod can shift it by
// its own size.
function daysFromCivil(y, m, d) {
  const yy = y - (m <= 2 ? 1 : 0);
  const era = Math.floor((yy >= 0 ? yy : yy - 399) / 400);
  const yoe = yy - era * 400; // [0, 399]
  const doy = Math.floor((153 * (m + (m > 2 ? -3 : 9)) + 2) / 5) + d - 1; // [0, 365]
  const doe = yoe * 365 + Math.floor(yoe / 4) - Math.floor(yoe / 100) + doy; // [0, 146096]
  return era * 146097 + doe - 719468; // days since 1970-01-01
}

function daysBetween(fromISO, toISO) {
  const f = parts(fromISO);
  const t = parts(toISO);
  return daysFromCivil(t.y, t.m, t.d) - daysFromCivil(f.y, f.m, f.d);
}

// The inclusive day count of a window: "2026-01-01" to "2026-01-01" is 1
// day, not 0. Exported so any component that needs to choose its own
// behaviour by a window's length -- TrendStrip's bucket choice, which
// must not reimplement date arithmetic itself -- uses this instead of
// hand-rolling it.
export function windowDayCount(fromISO, toISO) {
  return daysBetween(fromISO, toISO) + 1;
}

function currentMonthWindow() {
  const { y, m } = parts(todayISO());
  return `${y}-${pad(m)}-01..${y}-${pad(m)}-${pad(daysInMonth(y, m))}`;
}

export function shiftDays(iso, delta) {
  let { y, m, d } = parts(iso);
  d += delta;
  while (d < 1) {
    m -= 1;
    if (m < 1) { m = 12; y -= 1; }
    d += daysInMonth(y, m);
  }
  while (d > daysInMonth(y, m)) {
    d -= daysInMonth(y, m);
    m += 1;
    if (m > 12) { m = 1; y += 1; }
  }
  return `${y}-${pad(m)}-${pad(d)}`;
}

export function periodBounds(granularity, anchor) {
  if (granularity === "custom") {
    const { from, to } = splitCustom(anchor);
    return { startDate: from, endDate: to };
  }
  const { y, m } = parts(anchor);
  if (granularity === "day") {
    return { startDate: anchor, endDate: anchor };
  }
  if (granularity === "month") {
    return {
      startDate: `${y}-${pad(m)}-01`,
      endDate: `${y}-${pad(m)}-${pad(daysInMonth(y, m))}`,
    };
  }
  // Calendar year, deliberately: not the firm's 1 Jul - 30 Jun fiscal
  // year and not TimeEntry.year_end. See the spec, decision 7.
  return { startDate: `${y}-01-01`, endDate: `${y}-12-31` };
}

export function periodLabel(granularity, anchor) {
  if (granularity === "custom") {
    const { from, to } = splitCustom(anchor);
    if (from === to) return periodLabel("day", from);
    const f = parts(from);
    const t = parts(to);
    const fromLabel = `${f.d} ${MONTHS[f.m - 1].slice(0, 3)}` +
      (f.y !== t.y ? ` ${f.y}` : "");
    const toLabel = `${t.d} ${MONTHS[t.m - 1].slice(0, 3)} ${t.y}`;
    return `${fromLabel} – ${toLabel}`;
  }
  const { y, m, d } = parts(anchor);
  if (granularity === "year") return String(y);
  if (granularity === "month") return `${MONTHS[m - 1]} ${y}`;
  // Local-time three-argument constructor: safe, and used only to read a
  // weekday index back out.
  const weekday = WEEKDAYS[new Date(y, m - 1, d).getDay()];
  return `${weekday}, ${d} ${MONTHS[m - 1]} ${y}`;
}

export function stepPeriod(granularity, anchor, delta) {
  if (granularity === "custom") {
    // Shift the window by its own length, so consecutive windows are
    // contiguous and never overlap: forward, the new start picks up the
    // day after the old end; backward, the new end is the day before the
    // old start. length is the inclusive day count of the window.
    const { from, to } = splitCustom(anchor);
    const length = windowDayCount(from, to);
    const newStart = shiftDays(from, delta * length);
    const newEnd = shiftDays(newStart, length - 1);
    return `${newStart}..${newEnd}`;
  }
  const { y, m } = parts(anchor);
  if (granularity === "day") return shiftDays(anchor, delta);
  if (granularity === "year") return `${y + delta}-01-01`;
  // Normalise to the first of the month, so stepping back from 31 March
  // lands on 1 February rather than on a day February does not have.
  const total = y * 12 + (m - 1) + delta;
  return `${Math.floor(total / 12)}-${pad((total % 12) + 1)}-01`;
}

function isRealDate(y, m, d) {
  return (
    Number.isInteger(y) && Number.isInteger(m) && Number.isInteger(d) &&
    y >= 1900 && y <= 2999 && m >= 1 && m <= 12 &&
    d >= 1 && d <= daysInMonth(y, m)
  );
}

export function normalizeAnchor(granularity, raw) {
  if (granularity === "custom") {
    // A reversed range ("to" typed before "from") is an obvious slip
    // with one obvious intent, so swap rather than reject. Anything else
    // malformed falls back to a sensible default window -- the current
    // month -- never to a single day and never by throwing.
    if (typeof raw === "string") {
      const match = raw.match(/^(\d{4}-\d{2}-\d{2})\.\.(\d{4}-\d{2}-\d{2})$/);
      if (match) {
        const [, fromRaw, toRaw] = match;
        const f = parts(fromRaw);
        const t = parts(toRaw);
        if (isRealDate(f.y, f.m, f.d) && isRealDate(t.y, t.m, t.d)) {
          return fromRaw <= toRaw ? `${fromRaw}..${toRaw}` : `${toRaw}..${fromRaw}`;
        }
      }
    }
    return currentMonthWindow();
  }

  // A stale or hand-edited URL must fall back, never blank the page.
  const fallback = todayISO();
  if (typeof raw !== "string" || !raw) return normalizeAnchor(granularity, fallback);

  let iso = raw;
  if (/^\d{4}$/.test(raw)) iso = `${raw}-01-01`;
  else if (/^\d{4}-\d{2}$/.test(raw)) iso = `${raw}-01`;
  else if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return normalizeAnchor(granularity, fallback);

  const { y, m, d } = parts(iso);
  if (!isRealDate(y, m, d)) return normalizeAnchor(granularity, fallback);

  if (granularity === "year") return `${y}-01-01`;
  if (granularity === "month") return `${y}-${pad(m)}-01`;
  return iso;
}

export function anchorParam(granularity, anchor) {
  if (granularity === "custom") return anchor;
  const { y, m } = parts(anchor);
  if (granularity === "year") return String(y);
  if (granularity === "month") return `${y}-${pad(m)}`;
  return anchor;
}

/**
 * Derive everything a caller needs from a granularity and an anchor.
 *
 * State itself lives with the caller (EmployeesPage keeps it in the URL),
 * so this hook is a derivation plus two stable callbacks, not a store.
 */
export function usePeriod({ granularity, anchor, onChange }) {
  const bounds = useMemo(
    () => periodBounds(granularity, anchor), [granularity, anchor]);
  const label = useMemo(
    () => periodLabel(granularity, anchor), [granularity, anchor]);

  const step = useCallback(
    (delta) => onChange({
      granularity, anchor: stepPeriod(granularity, anchor, delta),
    }),
    [granularity, anchor, onChange]);

  // Converts the on-screen window rather than discarding it. Passing the
  // OLD granularity's raw anchor straight into normalizeAnchor(next, ...)
  // only works when both granularities share the same anchor shape --
  // entering custom, a plain "YYYY-MM-DD" fails the FROM..TO shape, and
  // leaving custom, a "FROM..TO" string matches no day/month/year
  // pattern -- so either direction silently falls back to "today"/"this
  // month" instead of converting. Going through periodBounds first keeps
  // both directions on the window that was actually on screen.
  const setGranularity = useCallback((next) => {
    let rawAnchor = anchor;
    if (next === "custom" && granularity !== "custom") {
      const onScreen = periodBounds(granularity, anchor);
      rawAnchor = `${onScreen.startDate}..${onScreen.endDate}`;
    } else if (next !== "custom" && granularity === "custom") {
      rawAnchor = periodBounds(granularity, anchor).startDate;
    }
    onChange({ granularity: next, anchor: normalizeAnchor(next, rawAnchor) });
  }, [anchor, granularity, onChange]);

  return { ...bounds, label, step, setGranularity };
}
